import Vue from 'vue';
import Router from 'vue-router';

import store from '@/store';
import {
  readForceEnroll2Fa,
  readInitialized,
  readIsLoggedIn,
} from '@/store/main/getters';
import {
  ensureLoggedIn,
  ensureLoggedOut,
  ensureLoggedInAdmin,
  ensureSignupEnabled,
} from './authGuards';
import { permissionGuard } from './permissionGuard';
import { redirectToChemicanLP } from './redirectToChemicanLP';

Vue.use(Router);

const router = new Router({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      component: () => import('../views/layouts/lpLayout/LPLayout.vue'),
      beforeEnter: (to, _, next) => {
        if (to.hash !== '/') {
          next();
          return;
        }

        return redirectToChemicanLP('/')(to, _, next);
      },
      children: [
        {
          path: '',
          name: 'top',
          beforeEnter: redirectToChemicanLP('/'),
        },
        {
          path: 'corporate',
          name: 'corporate',
          beforeEnter: redirectToChemicanLP('/corporate'),
        },
        {
          path: 'login',
          name: 'login',
          beforeEnter: ensureLoggedOut,
          // route level code-splitting
          // this generates a separate chunk (about.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () =>
            import(
              /* webpackChunkName: "login" */ '../views/pages/login/LoginPage.vue'
            ),
          props: (route) => ({
            messageText: route.params.messageText,
            messageKey: route.params.messageKey,
            messageType: route.params.messageType,
          }),
        },
        {
          path: '/login/verify',
          name: 'loginVerify',
          beforeEnter: ensureLoggedOut,
          component: () =>
            import(
              /* webpackChunkName: "two-factor-verify" */ '../views/main/general/LoginVerifyPageComponent.vue'
            ),
        },
        {
          path: '/saml/login',
          name: 'samlLogin',
          beforeEnter: ensureLoggedOut,
          component: () =>
            import(
              /* webpackChunkName: "saml-login" */ '../views/pages/samlLogin/SamlLoginPage.vue'
            ),
        },
        {
          path: '/saml/callback',
          name: 'samlCallback',
          beforeEnter: ensureLoggedOut,
          component: () =>
            import(
              /* webpackChunkName: "saml-callback" */ '../views/pages/samlLogin/SamlCallbackPage.vue'
            ),
        },
        {
          path: '/saml/error',
          name: 'samlLoginError',
          beforeEnter: ensureLoggedOut,
          component: () =>
            import(
              /* webpackChunkName: "saml-login-error" */ '../views/pages/samlLogin/SamlLoginErrorPage.vue'
            ),
        },
        {
          path: '/saml/complete',
          name: 'samlComplete',
          component: () =>
            import(
              /* webpackChunkName: "saml-complete" */ '../views/pages/samlLogin/SamlLoginCompletePage.vue'
            ),
        },
        {
          path: 'signup/activation',
          name: 'activation',
          component: () =>
            import(
              /* webpackChunkName: "userActivation" */ '../views/pages/signUpActivation/UserActivationPage.vue'
            ),
        },
        {
          path: 'signup/complete',
          name: 'signUpComplete',
          component: () =>
            import(
              /* webpackChunkName: "signupComplete" */ '../views/pages/signUpComplete/SignUpCompletePage.vue'
            ),
        },
        // The signup page is only accessible in development environment.
        {
          path: 'signup',
          name: 'signUp',
          beforeEnter: (from, to, next) => {
            ensureSignupEnabled(from, to, (result) => {
              if (result === undefined) {
                ensureLoggedOut(from, to, next);
              } else {
                next(result);
              }
            });
          },
          component: () =>
            import(
              /* webpackChunkName: "login" */ '../views/pages/signUp/SignUpPage.vue'
            ),
        },
        {
          path: 'inquiry',
          name: 'inquiry',
          beforeEnter: redirectToChemicanLP('contact'),
        },
        {
          path: 'en/contact',
          name: 'contacten',
          component: () =>
            import(
              /* webpackChunkName: "login" */ '../views/pages/enContact/EnContactPage.vue'
            ),
        },
        {
          path: 'reset-password',
          name: 'resetPassword',
          component: () =>
            import(
              /* webpackChunkName: "login" */ '../views/pages/resetPassword/ResetPasswordPage.vue'
            ),
        },
        {
          path: 'password-recovery',
          name: 'passwordRecovery',
          component: () =>
            import(
              /* webpackChunkName: "login" */ '../views/pages/passwordRecovery/PasswordRecoveryPage.vue'
            ),
        },
        {
          path: 'unregister',
          name: 'unregister',
          component: () =>
            import(
              /* webpackChunkName: "user" */ '../views/pages/unregister/UnregisterPage.vue'
            ),
        },
      ],
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () =>
        import('../views/layouts/dashboardLayout/DashboardLayout.vue'),
      beforeEnter: ensureLoggedIn,
      children: [
        {
          path: 'sds/upload',
          name: 'sdsUpload',
          component: () =>
            import(
              /* webpackChunkName: "sds-upload" */ '../views/pages/sdsUpload/SdsUploadPage.vue'
            ),
          beforeEnter: permissionGuard('SDS_UPLOAD', 'sdsList'),
        },
        {
          path: 'sds/index',
          name: 'sdsList',
          component: () =>
            import(
              /* webpackChunkName: "sds-list" */ '../views/pages/sdsIndex/SdsIndexPage.vue'
            ),
        },
        {
          path: 'supplier-inquiry',
          name: 'supplierInquiry',
          component: () =>
            import(
              /* webpackChunkName: "supplier-inquiry" */ '../views/pages/supplierInquiry/SupplierInquiryPage.vue'
            ),
        },
        {
          path: 'dynamic/list',
          name: 'sdsDynamicList',
          component: () =>
            import(
              /* webpackChunkName: "processing-sds" */ '../views/pages/dynamicList/DynamicListPage.vue'
            ),
          beforeEnter: permissionGuard('CHECK_LIST_READ', 'sdsList'),
        },
        {
          path: 'dynamic/list/templates',
          name: 'sdsDynamicListTemplates',
          component: () =>
            import(
              /* webpackChunkName: "processing-sds" */ '../views/pages/dynamicList/DynamicListTemplatesPage.vue'
            ),
          beforeEnter: permissionGuard('CHECK_LIST_READ', 'sdsList'),
        },
        {
          path: 'dynamic/list/templates/:templateId',
          name: 'sdsDynamicListTemplateDetail',
          component: () =>
            import(
              /* webpackChunkName: "processing-sds" */ '../views/pages/dynamicList/DynamicListTemplateDetailPage.vue'
            ),
          beforeEnter: permissionGuard('CHECK_LIST_READ', 'sdsList'),
        },
        {
          path: 'dynamic/list/:id',
          name: 'sdsDynamicListResult',
          component: () =>
            import(
              /* webpackChunkName: "processing-sds" */ '../views/pages/dynamicListDetail/DynamicListResultPage.vue'
            ),
          beforeEnter: permissionGuard('CHECK_LIST_READ', 'sdsList'),
        },
        {
          path: 'sds/index/error',
          name: 'sdsError',
          component: () =>
            import(
              /* webpackChunkName: "error-sds" */ '../views/pages/sdsIndexError/SdsIndexErrorPage.vue'
            ),
        },
        {
          path: 'company/departments',
          name: 'companyDepartments',
          component: () =>
            import(
              /* webpackChunkName: "company-departments" */ '../views/pages/companyDepartments/CompanyDepartmentsPage.vue'
            ),
        },
        {
          path: 'company/users',
          name: 'companyUsers',
          component: () =>
            import(
              /* webpackChunkName: "company-users" */ '../views/pages/companyUsers/CompanyUsersPage.vue'
            ),
        },
        {
          path: 'company/tags',
          name: 'companyTags',
          component: () =>
            import(
              /* webpackChunkName: "company-tags" */ '../views/pages/companyTags/CompanyTagsPage.vue'
            ),
        },
        {
          path: 'company/periodic_export',
          name: 'companyPeriodicExport',
          component: () =>
            import(
              /* webpackChunkName: "company-periodic-export" */ '../views/pages/companyPeriodicExport/PeriodicExportPage.vue'
            ),
        },
        {
          path: 'company',
          name: 'company',
          component: () =>
            import(
              /* webpackChunkName: "company" */ '../views/pages/company/CompanyPage.vue'
            ),
        },
        {
          path: 'company/settings',
          name: 'companySettings',
          component: () =>
            import(
              /* webpackChunkName: "company-settings" */ '../views/pages/companySettings/CompanySettingsPage.vue'
            ),
        },
        {
          path: 'user',
          name: 'user',
          component: () =>
            import(
              /* webpackChunkName: "user" */ '../views/pages/user/UserPage.vue'
            ),
        },
      ],
    },
    {
      path: '/security',
      name: 'security',
      component: () =>
        import('../views/layouts/securityLayout/SecurityLayout.vue'),
      beforeEnter: ensureLoggedInAdmin,
      children: [
        {
          path: 'ip_address_restriction',
          name: 'ipAddressRestriction',
          component: () =>
            import(
              /* webpackChunkName: "ip-address-restriction" */ '../views/pages/ipAddressRestriction/IpAddressRestrictionPage.vue'
            ),
        },
        {
          path: 'password_setting',
          name: 'passwordSetting',
          component: () =>
            import(
              '../views/pages/passwordSetting/ConditionalPasswordSettingPage.vue'
            ),
        },
        {
          path: 'enforce_2fa',
          name: 'enforce2Fa',
          component: () =>
            import(
              /* webpackChunkName: "enforce-2fa" */ '../views/pages/enforce2Fa/Enforce2FaPage.vue'
            ),
        },
        {
          path: 'saml_setting',
          name: 'samlSetting',
          component: () =>
            import(
              /* webpackChunkName: "ip-address-restriction" */ '../views/pages/samlSetting/SamlSettingPage.vue'
            ),
        },
      ],
    },
    {
      path: '/2fa/enroll/:mode(totp|phone_code)',
      name: 'twoFactorSetup',
      beforeEnter: ensureLoggedIn,
      component: () =>
        import(
          /* webpackChunkName: "two-factor-setup" */ '../views/main/general/TwoFactorSetup.vue'
        ),
      props: (route) => ({ mode: route.params.mode }), // pass the mode as a prop
    },
    {
      path: '/*',
      redirect: '/',
    },
  ],
  // @ts-ignore
  scrollBehavior(to, _, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { selector: to.hash };
    }

    return { x: 0, y: 0 };
  },
});

const waitUntilInitialized = () =>
  new Promise((resolve) => {
    if (readInitialized(store)) {
      resolve(true);
    } else {
      store.watch(
        (state) => state.main.initialized,
        (initialized) => {
          if (initialized) {
            resolve(true);
          }
        }
      );
    }
  });

// Make sure that the app is initialized before executing any routing.
router.beforeEach(async (to, __, next) => {
  await waitUntilInitialized();
  // 二要素認証が必須化されていて未設定のユーザーは、設定が完了するまでマイページと設定画面のみ許可する。
  const allowedRouteNames = ['user', 'twoFactorSetup'];
  if (
    readIsLoggedIn(store) &&
    readForceEnroll2Fa(store) &&
    !allowedRouteNames.includes(to.name ?? '')
  ) {
    next({ name: 'user' });
    return;
  }
  next();
});

export default router;
